import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

// Tipos de notificación (mapeados con el backend)
export const NOTIFICATION_TYPES = {
  BIRTHDAY: 'birthday',
  ANNIVERSARY: 'anniversary',
  POLICY_RENEWAL: 'renewal',
  LEAD_CONTACT: 'lead_contact',
  CHARGEBACK: 'chargeback',
  POLICY_PENDING: 'pending',
  LEAD: 'lead',
  PAYMENT: 'payment',
  MARKETING: 'marketing',
  SYSTEM: 'system',
  GENERAL: 'general',
  REFERRAL_REQUEST: 'referral_request',
  LEAD_BIRTHDAY: 'lead_birthday',
  LEAD_FOLLOW_UP: 'lead_follow_up',
  MESSENGER_LEAD: 'messenger_lead',
  INSTAGRAM_LEAD: 'instagram_lead',
  WHATSAPP_LEAD: 'whatsapp_lead',
  APORTACION_OPLUS: 'aportacion_oplus',
  CALENDAR_REMINDER: 'calendar_reminder',
};

// Prioridades
export const NOTIFICATION_PRIORITY = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low'
};

// Mapeo de tipo a prioridad
const typePriorityMap = {
  [NOTIFICATION_TYPES.CHARGEBACK]: NOTIFICATION_PRIORITY.HIGH,
  [NOTIFICATION_TYPES.BIRTHDAY]: NOTIFICATION_PRIORITY.MEDIUM,
  [NOTIFICATION_TYPES.ANNIVERSARY]: NOTIFICATION_PRIORITY.LOW,
  [NOTIFICATION_TYPES.POLICY_RENEWAL]: NOTIFICATION_PRIORITY.HIGH,
  [NOTIFICATION_TYPES.POLICY_PENDING]: NOTIFICATION_PRIORITY.MEDIUM,
  [NOTIFICATION_TYPES.LEAD]: NOTIFICATION_PRIORITY.LOW,
  [NOTIFICATION_TYPES.PAYMENT]: NOTIFICATION_PRIORITY.MEDIUM,
  [NOTIFICATION_TYPES.SYSTEM]: NOTIFICATION_PRIORITY.LOW,
  [NOTIFICATION_TYPES.LEAD_BIRTHDAY]: NOTIFICATION_PRIORITY.MEDIUM,
  [NOTIFICATION_TYPES.LEAD_FOLLOW_UP]: NOTIFICATION_PRIORITY.MEDIUM,
  [NOTIFICATION_TYPES.MESSENGER_LEAD]: NOTIFICATION_PRIORITY.HIGH,
  [NOTIFICATION_TYPES.INSTAGRAM_LEAD]: NOTIFICATION_PRIORITY.HIGH,
  [NOTIFICATION_TYPES.WHATSAPP_LEAD]: NOTIFICATION_PRIORITY.HIGH,
  [NOTIFICATION_TYPES.APORTACION_OPLUS]: NOTIFICATION_PRIORITY.HIGH,
};

export const NotificationProvider = ({ children }) => {
  const { user, logout } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Obtener token del localStorage
  const getAuthToken = useCallback(() => {
    const token = localStorage.getItem('token');
    return token;
  }, []);

  // Cargar notificaciones del backend
  const fetchNotifications = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      setLoading(true);
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Transformar notificaciones del backend al formato del frontend
      const transformedNotifications = response.data.map(notif => ({
        id: notif.id,
        type: notif.type || NOTIFICATION_TYPES.SYSTEM,
        title: notif.title,
        message: notif.message,
        read: notif.is_read || false,
        priority: typePriorityMap[notif.type] || NOTIFICATION_PRIORITY.LOW,
        entityId: notif.entity_id,
        entityType: notif.entity_type,
        jobId: notif.job_id,
        actionData: notif.action_data || null,
        createdAt: notif.created_at
      }));
      
      setNotifications(transformedNotifications);
      setUnreadCount(transformedNotifications.filter(n => !n.read).length);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        return;
      }
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthToken]);

  // Cargar notificaciones al inicio y cuando el usuario cambia (login/logout)
  useEffect(() => {
    if (user) {
      fetchNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user, fetchNotifications]);

  // Polling cada 60 segundos — solo cuando hay sesión activa y la cuenta no está suspendida
  useEffect(() => {
    if (!user || user.account_suspended) return;

    const interval = setInterval(() => {
      fetchNotifications();
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [user, fetchNotifications]);

  // Marcar como leída en backend
  const markAsRead = useCallback(async (notificationId) => {
    const token = getAuthToken();
    
    // Actualizar localmente primero (optimistic update)
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    if (token) {
      try {
        await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
  }, [getAuthToken]);

  // Marcar todas como leídas
  const markAllAsRead = useCallback(async () => {
    const token = getAuthToken();
    
    // Actualizar localmente
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);

    if (token) {
      try {
        await axios.put(`${API}/notifications/mark-all-read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error marking all notifications as read:', error);
      }
    }
  }, [getAuthToken, notifications]);

  // Eliminar notificación (solo local por ahora)
  const removeNotification = useCallback((notificationId) => {
    setNotifications(prev => {
      const notif = prev.find(n => n.id === notificationId);
      if (notif && !notif.read) {
        setUnreadCount(count => Math.max(0, count - 1));
      }
      return prev.filter(n => n.id !== notificationId);
    });
  }, []);

  // Limpiar notificaciones leídas
  const clearRead = useCallback(async () => {
    const token = getAuthToken();
    
    if (token) {
      try {
        await axios.delete(`${API}/notifications/clear-read`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error clearing read notifications:', error);
      }
    }
    
    setNotifications(prev => prev.filter(n => !n.read));
  }, [getAuthToken]);

  // Limpiar todas (local)
  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Agregar notificación local (para compatibilidad)
  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
      ...notification,
      read: false,
      priority: notification.priority || typePriorityMap[notification.type] || NOTIFICATION_PRIORITY.LOW,
      createdAt: new Date().toISOString()
    };
    
    setNotifications(prev => [newNotification, ...prev].slice(0, 100));
    setUnreadCount(prev => prev + 1);
    return newNotification.id;
  }, []);

  // Refrescar notificaciones
  const refresh = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const value = {
    notifications,
    unreadCount,
    loading,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearRead,
    clearAll,
    refresh
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
